import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';

import { PrivilegesSnapshot, TimeService, utils } from '@bayclubs/utils';
import lodash from 'lodash';
import moment from 'moment';
import moment_tz from 'moment-timezone';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { SubstitutionBoard, SubstitutionBoardEnumerations } from './../../../interfaces/interfaces.module';
import { DataFilteringService, FiltersStorageService } from './../../../shared/shared.module';

import { ClubModel } from '@bayclubs/employee-management-api';
import { CategoryModel, ContextModel, InstructorModel } from '@bayclubs/group-classes-employee-api';
import { SubBoardListItemModel } from '../../models/sub-board-list-item-model';
import { BcValidators } from '../../services/bc-validators';
import { SubBoardListGatewayApiService as SubBoardListApiService } from '../../services/sub-board-list-api-gateway.service';
import { SubBoardRequestsCacheService } from '../../services/sub-board-requests-cache.service';

@Component({
    selector: 'app-sub-board',
    templateUrl: './sub-board.component.html',
    styleUrls: ['./sub-board.component.scss'],
    standalone: false
})
export class SubBoardComponent implements OnInit, OnDestroy {
    private initialQueryParams: ParamMap;
    private subscriptions: Subscription[] = [];
    private initialClub: ClubModel;

    public searchForm: UntypedFormGroup;

    context: ContextModel;
    requestStatuses: SubstitutionBoardEnumerations.SubstitutionRequestStatus[];
    categories: CategoryModel[] = [];
    privilegesSnapshot: SubstitutionBoard.SubstitutionBoardPrivileges;
    dateFromPickerControl: UntypedFormControl = new UntypedFormControl(null, []);
    dateToPickerControl: UntypedFormControl = new UntypedFormControl(null, []);

    @ViewChild('rowAdvancedSearch', { static: true }) advModeRowElementRef!: ElementRef;

    public urgentRequests: SubBoardListItemModel[] = [];
    public upcomingRequests: SubBoardListItemModel[] = [];
    public clubs: ClubModel[];
    public instructors: InstructorModel[];
    public subBoardIsEmpty = false;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private listApi: SubBoardListApiService,
        private filtersStorageService: FiltersStorageService,
        private timeService: TimeService,
        private subBoardRequestsCacheService: SubBoardRequestsCacheService,
        private filteringService: DataFilteringService
    ) {
        const routeSnapshot = route.snapshot;

        this.context = routeSnapshot.data.context;

        this.initialQueryParams = routeSnapshot.queryParamMap;
        this.requestStatuses = this.getRequestStatuses();

        const globalPrivilegesSnapshot: PrivilegesSnapshot = routeSnapshot.data.privilegesSnapshot;

        this.privilegesSnapshot = globalPrivilegesSnapshot.getPrivileges('SubBoard');

        this.clubs = this.context.clubs.filter(c => this.privilegesSnapshot.canViewSubstitutionRequests.indexOf(c.id) >= 0);
    }

    ngOnInit() {
        this.initialClub = this.getInitialClub(this.clubs);

        const today = moment().format('YYYY-MM-DD');
        const bcTodayControlValue = { value: today };
        const textTodayControlValue = moment().format('MM/DD/YYYY');

        this.mergeQueryParam({ clubId: this.initialClub.id } as Params);

        this.dateFromPickerControl.setValue(bcTodayControlValue);
        this.searchForm = this.formBuilder.group({
            clubId: [this.initialClub.id, Validators.required],
            categoryCodes: null,
            instructorId: null,
            advancedMode: [false, Validators.required],
            dateFrom: [textTodayControlValue, BcValidators.bcDateStringValidator],
            dateTo: [null, BcValidators.bcDateStringValidator],
            status: SubstitutionBoardEnumerations.SubstitutionRequestStatus.open.code,
        });

        this.followFormCategoriesByClub(this.initialClub.id);
        this.filterInstructorsByClub(this.initialClub.id);
        this.subscribeToFormControlChangesToUpdateQueryParams();
        this.subscribeToQueryParamsChanges();

        this.handleFilterDatesInteractions();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    get allRequests(): SubBoardListItemModel[] {
        return this.urgentRequests.concat(this.upcomingRequests);
    }

    get isSimpleMode(): boolean {
        const v = !this.searchForm.get('advancedMode').value;
        return v;
    }

    set isSimpleMode(isInSimpleMode: boolean) {
        this.searchForm.get('advancedMode').setValue(!isInSimpleMode);
    }

    viewModeChanged(checked: boolean) {
        // NOTE: wait for the filter collapse/expand to not to blink the result list twice
        setTimeout(() => {
            this.clearResults();
            this.isSimpleMode = checked;
        }, 350);
    }

    resetDateTo(options: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
        this.dateToPickerControl.reset(null, options);
        const control = this.searchForm.controls['dateTo'];
        control.reset(null, options);
    }

    resetDateFrom(options: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
        this.dateFromPickerControl.reset(null, options);
        const control = this.searchForm.controls['dateFrom'];
        control.reset(null, options);
    }

    private showAdvancedPane(show: boolean): void {
        if (show) {
            this.advModeRowElementRef.nativeElement.classList.add('show');
        } else {
            this.advModeRowElementRef.nativeElement.classList.remove('show');
        }
    }

    private handleFilterDatesInteractions() {
        const datePickerDateFromSub = this.dateFromPickerControl.valueChanges
            .pipe(
                filter(v => v != null),
                distinctUntilChanged((prev, curr) => prev.value === curr.value)
            )
            .subscribe((v: utils.BcDate) => {
                const momentDate = moment(v.value, 'YYYY-MM-DD', true);
                this.searchForm.get('dateFrom').setValue(momentDate.format('MM/DD/YYYY'));
            });

        this.subscriptions.push(datePickerDateFromSub);

        // NOTE: a distinct method call required due to the mask plugin issue with unwanted valuechanges raised events
        // https://github.com/JsDaddy/ngx-mask/issues/471
        const dateFromSub = this.searchForm
            .get('dateFrom')
            .valueChanges.pipe(distinctUntilChanged((prev, curr) => prev === curr))
            .subscribe((v: string) => {
                const control = this.searchForm.get('dateFrom');
                if (control.valid) {
                    let newPickerDate: utils.BcDate;
                    if (v === null) {
                        newPickerDate = null;
                    } else {
                        newPickerDate = this.timeService.toBcDate(moment(v, 'MM/DD/YYYY', true));
                    }
                    this.dateFromPickerControl.setValue(newPickerDate, { emitEvent: false });
                }
            });

        this.subscriptions.push(dateFromSub);

        const datePickerDateToSub = this.dateToPickerControl.valueChanges
            .pipe(
                filter(v => v != null),
                distinctUntilChanged((prev, curr) => prev.value === curr.value)
            )
            .subscribe((v: utils.BcDate) => {
                const momentDate = moment(v.value, 'YYYY-MM-DD', true);
                this.searchForm.get('dateTo').setValue(momentDate.format('MM/DD/YYYY'));
            });

        this.subscriptions.push(datePickerDateToSub);

        // NOTE: a distinct method call required due to the mask plugin issue with unwanted valuechanges raised events
        // https://github.com/JsDaddy/ngx-mask/issues/471
        const dateToSub = this.searchForm
            .get('dateTo')
            .valueChanges.pipe(distinctUntilChanged((prev, curr) => prev === curr))
            .subscribe((v: string) => {
                const control = this.searchForm.get('dateTo');
                if (control.valid) {
                    let newPickerDate: utils.BcDate;
                    if (v === null) {
                        newPickerDate = null;
                    } else {
                        newPickerDate = this.timeService.toBcDate(moment(v, 'MM/DD/YYYY', true));
                    }
                    this.dateToPickerControl.setValue(newPickerDate, { emitEvent: false });
                }
            });

        this.subscriptions.push(dateToSub);
    }

    private assignAvailableCategoriesByClub(clubId: string): void {
        this.categories = this.filteringService.getAvailableCategoriesByClubs<CategoryModel, string>([clubId], this.context);
    }

    private subscribeToFormControlChangesToUpdateQueryParams() {
        this.onFormControlChange('clubId', value => {
            this.followFormCategoriesByClub(value);
            this.filterInstructorsByClub(value);

            const clubParam: Params = { clubId: value, provider: [], category: [] };
            this.mergeQueryParam(clubParam);
        });

        this.onFormControlChange('advancedMode', value => {
            const params: Params = {};
            if (!value) {
                params['adv'] = params['from'] = params['to'] = params['status'] = null;
            } else {
                params['adv'] = 1;
                params['from'] = (this.dateFromPickerControl.value && this.dateFromPickerControl.value.value) || null;
                params['to'] = (this.dateToPickerControl.value && this.dateToPickerControl.value.value) || null;
                params['status'] = this.searchForm.get('status').value;
            }
            this.mergeQueryParam(params);
        });

        this.onCategoryChangeMergeParam();
        this.onFormControlChangeMergeParam('instructorId', 'provider');
        this.onFormControlChangeMergeParam(
            'dateFrom',
            'from',
            v => (this.dateFromPickerControl.value && this.dateFromPickerControl.value.value) || null
        );

        this.onFormControlChangeMergeParam(
            'dateTo',
            'to',
            v => (this.dateToPickerControl.value && this.dateToPickerControl.value.value) || null
        );

        this.onFormControlChangeMergeParam('status', 'status');
    }

    private filterInstructorsByClub(clubId: string) {
        this.instructors = this.context.instructors.filter(
            i => clubId === undefined || clubId === i.homeClubId || i.clubIds.includes(clubId)
        );

        const instructorControl = this.searchForm.controls['instructorId'];
        instructorControl.setValue(null, { emitEvent: false });
    }

    private onCategoryChangeMergeParam() {
        const sub = this.searchForm.controls['categoryCodes'].valueChanges.subscribe(value => {
            const categoriesCodes = value.map(item => {
                return item.code;
            });
            const param: Params = { category: categoriesCodes };
            this.mergeQueryParam(param);
        });

        this.subscriptions.push(sub);
    }

    private onFormControlChangeMergeParam(formControlName: string, paramName: string, setValue?: (value: any) => any): void {
        const sub = this.searchForm.controls[formControlName].valueChanges.subscribe(value => {
            const param: Params = { [paramName]: setValue != null ? setValue(value) : value };
            this.mergeQueryParam(param);
        });

        this.subscriptions.push(sub);
    }

    private onFormControlChange(formControlName: string, valueChangedCallback: (value: any) => void): void {
        const sub = this.searchForm.controls[formControlName].valueChanges.subscribe(value => valueChangedCallback(value));

        this.subscriptions.push(sub);
    }

    private followFormCategoriesByClub(selectedClub: string): void {
        this.assignAvailableCategoriesByClub(selectedClub);
        const categoriesControl = this.searchForm.controls['categoryCodes'];
        categoriesControl.setValue(null, { emitEvent: false });
    }

    private mergeQueryParam(params: Params): void {
        this.router
            .navigate([], {
                relativeTo: this.route,
                queryParams: params,
                queryParamsHandling: 'merge',
            })
            .then(() => (this.filtersStorageService.subBoardUrl = this.router.url));
    }

    private subscribeToQueryParamsChanges(): void {
        const sub = this.route.queryParamMap
            .pipe(
                debounceTime(200),
                tap(p => {
                    if (p.has('adv') && p.get('adv') === '1') {
                        if (p.has('from')) {
                            this.setControlDateSilently(p.get('from'), 'dateFrom', this.dateFromPickerControl);
                        } else {
                            this.resetDateFrom({ emitEvent: false });
                        }

                        if (p.has('to')) {
                            this.setControlDateSilently(p.get('to'), 'dateTo', this.dateToPickerControl);
                        } else {
                            this.resetDateTo({ emitEvent: false });
                        }

                        if (p.has('status')) {
                            this.searchForm.get('status').setValue(p.get('status'), { emitEvent: false });
                        } else {
                            this.searchForm.get('status').reset(null, { emitEvent: false });
                        }

                        this.searchForm.get('advancedMode').setValue(true, { emitEvent: false });
                        this.showAdvancedPane(true);
                    } else {
                        this.searchForm.get('advancedMode').setValue(false, { emitEvent: false });
                        this.showAdvancedPane(false);
                    }

                    if (p.has('clubId')) {
                        this.searchForm.get('clubId').setValue(p.get('clubId'), { emitEvent: false });
                    } else {
                        this.searchForm.get('clubId').setValue(this.initialClub.id, { emitEvent: false });
                    }

                    this.followFormCategoriesByClub(this.searchForm.get('clubId').value);

                    if (p.has('provider')) {
                        this.searchForm.get('instructorId').setValue(p.get('provider'), { emitEvent: false });
                    } else {
                        this.searchForm.get('instructorId').reset(null, { emitEvent: false });
                    }

                    if (p.has('category')) {
                        const categoriesCodes: string[] = p.getAll('category');
                        const categories: CategoryModel[] = this.context.categories.filter(c =>
                            lodash.includes(categoriesCodes, c.code)
                        );
                        this.searchForm.get('categoryCodes').setValue(categories, { emitEvent: false });
                    } else {
                        this.searchForm.get('categoryCodes').reset(null, { emitEvent: false });
                    }
                }),
                switchMap(_ => this.getRefreshedRequests())
            )
            .subscribe(r => this.onNewRequestsLoaded(r));

        this.subscriptions.push(sub);
    }

    private setControlDateSilently(momentDateString: string, formControlName: string, targetPicker: UntypedFormControl): void {
        const mom = moment(momentDateString);

        if (mom.isValid()) {
            targetPicker.setValue({ value: mom.format('YYYY-MM-DD') }, { emitEvent: false });
            this.searchForm.get(formControlName).setValue(mom.format('MM/DD/YYYY'), { emitEvent: false });
        }
    }

    private getInitialClub(allClubs: ClubModel[]): ClubModel {
        const clubIdFromParams = this.initialQueryParams.get('clubId');
        if (clubIdFromParams) {
            const clubFromParams = allClubs.find(c => c.id === clubIdFromParams);
            if (clubFromParams) {
                return clubFromParams;
            }
        }

        return allClubs[0];
    }

    private clearResults(): void {
        this.urgentRequests = [];
        this.upcomingRequests = [];
    }

    private getRefreshedRequests(): Observable<SubBoardListItemModel[]> {
        if (this.searchForm.invalid) {
            return EMPTY;
        }

        this.clearResults();

        const advancedMode = this.searchForm.get('advancedMode').value;
        const clubId = this.searchForm.get('clubId').value;
        const instructorId = this.searchForm.get('instructorId').value;

        let categoryCodes = [];
        if (this.searchForm.get('categoryCodes').value) {
            categoryCodes = this.searchForm.get('categoryCodes').value.map(c => c.code);
        }
        const dateFrom = this.timeService.toBcDate(this.searchForm.get('dateFrom').value);

        const dateToText = this.searchForm.get('dateTo').value;
        const dateTo = dateToText ? this.timeService.toBcDate(dateToText) : null;

        const status = this.searchForm.get('status').value;

        this.subBoardIsEmpty = false;

        if (advancedMode === true) {
            return this.listApi.getSubBoardListItemsAdvanced(clubId, instructorId, categoryCodes, dateFrom, dateTo, status);
        } else {
            return this.listApi.getSubBoardListItems(clubId, instructorId, categoryCodes);
        }
    }

    private onNewRequestsLoaded(requests: SubBoardListItemModel[]) {
        if (!requests || requests.length === 0) {
            this.subBoardIsEmpty = true;
        }

        this.subBoardRequestsCacheService.refreshCache(requests);

        for (const request of requests) {
            const hoursToStart = this.hoursToStart(request.classDate, request.classTimeInMinutesFrom, request.clubId);

            const isUrgent = hoursToStart < 48;
            if (isUrgent) {
                this.urgentRequests.push(request);
            } else {
                this.upcomingRequests.push(request);
            }
        }

        this.urgentRequests = this.orderRequestsByTime(this.urgentRequests);
        this.upcomingRequests = this.orderRequestsByTime(this.upcomingRequests);
    }

    private hoursToStart(date: utils.BcDate, timeInMinutesFrom: number, clubId: string): number {
        const clubTimezone = this.context.clubs.find(c => c.id === clubId).timezone;

        const classDateAsMoment: moment_tz.Moment = moment_tz.tz(date.value, clubTimezone);
        const classStartAsMoment = classDateAsMoment.add(timeInMinutesFrom, 'minutes');

        const now: moment_tz.Moment = (<any>moment()).tz(clubTimezone);

        const hoursToStart = classStartAsMoment.diff(now.utc(), 'hours');

        return hoursToStart;
    }

    private orderRequestsByTime(requests: SubBoardListItemModel[]) {
        return requests.sort((lhs, rhs) => {
            const lhsDate = moment(lhs.classDate.value);
            const rhsDate = moment(rhs.classDate.value);

            if (lhsDate.isSame(rhsDate)) {
                return lhs.classTimeInMinutesFrom - rhs.classTimeInMinutesFrom;
            }

            return lhsDate.isBefore(rhsDate) ? -1 : 1;
        });
    }

    private getRequestStatuses(): any[] {
        const statuses: SubstitutionBoardEnumerations.EnumerationModel[] = [];

        for (const singleField in SubstitutionBoardEnumerations.SubstitutionRequestStatus) {
            if (SubstitutionBoardEnumerations.SubstitutionRequestStatus.hasOwnProperty(singleField)) {
                const singleStatus = SubstitutionBoardEnumerations.SubstitutionRequestStatus[singleField];
                statuses.push(singleStatus);
            }
        }

        return statuses;
    }
}
